import { useState } from "react";
import {
  Plus,
  Trash2,
  StickyNote,
  Phone,
  Users,
  Eye,
  FileText,
  MessageSquare,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

interface NotesLogProps {
  notes: any[];
  onNotesChange: (notes: any[]) => void;
  dealId?: string;
}

const NOTE_TYPES = [
  {
    value: "general",
    label: "General Note",
    icon: StickyNote,
    color: "bg-blue-100 text-blue-800",
  },
  {
    value: "call",
    label: "Phone Call",
    icon: Phone,
    color: "bg-green-100 text-green-800",
  },
  {
    value: "meeting",
    label: "Meeting",
    icon: Users,
    color: "bg-purple-100 text-purple-800",
  },
  {
    value: "observation",
    label: "Observation",
    icon: Eye,
    color: "bg-amber-100 text-amber-800",
  },
  {
    value: "analysis",
    label: "Analysis",
    icon: FileText,
    color: "bg-indigo-100 text-indigo-800",
  },
  {
    value: "followup",
    label: "Follow-up",
    icon: MessageSquare,
    color: "bg-cyan-100 text-cyan-800",
  },
  {
    value: "issue",
    label: "Issue",
    icon: AlertCircle,
    color: "bg-red-100 text-red-800",
  },
];

export function NotesLog({ notes, onNotesChange, dealId }: NotesLogProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [noteAuthor, setNoteAuthor] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleAdd = () => {
    if (!newNote.trim()) return;

    const selectedType = NOTE_TYPES.find((t) => t.value === noteType);
    const icon = selectedType?.icon || StickyNote;

    const note: any = {
      id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: newNote.trim(),
      type: noteType,
      author: noteAuthor.trim() || "Anonymous",
      timestamp: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dealId: dealId,
    };

    onNotesChange([note, ...notes]);
    setNewNote("");
    setNoteAuthor("");
    setNoteType("general");
    setIsAdding(false);
  };

  const handleDelete = () => {
    if (!deleteId) return;
    onNotesChange(notes.filter((n) => n.id !== deleteId));
    setDeleteId(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 24) {
      if (diffHours === 0) return "Just now";
      if (diffHours === 1) return "1 hour ago";
      return `${diffHours} hours ago`;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTypeConfig = (type: string) => {
    return NOTE_TYPES.find((t) => t.value === type) || NOTE_TYPES[0];
  };

  const IconComponent = getTypeConfig(noteType).icon;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <StickyNote className="h-4 w-4" />
            Notes Log
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Track calls, meetings, observations and important details
          </p>
        </div>
        {!isAdding && (
          <Button size="sm" variant="outline" onClick={() => setIsAdding(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Note
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isAdding && (
          <div className="space-y-3 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <IconComponent className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <Select value={noteType} onValueChange={setNoteType}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select note type" />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTE_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Input
                placeholder="Your name (optional)"
                value={noteAuthor}
                onChange={(e) => setNoteAuthor(e.target.value)}
                className="text-sm"
              />
              <Textarea
                placeholder={`Add your ${getTypeConfig(noteType).label.toLowerCase()}...`}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                autoFocus
                className="resize-none"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setIsAdding(false);
                  setNewNote("");
                  setNoteAuthor("");
                  setNoteType("general");
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-primary-blue"
                onClick={handleAdd}
                disabled={!newNote.trim()}
              >
                Save Note
              </Button>
            </div>
          </div>
        )}

        {notes.length === 0 && !isAdding ? (
          <div className="text-center py-8 text-muted-foreground">
            <StickyNote className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No notes yet</p>
            <p className="text-xs mt-1 max-w-sm mx-auto">
              Add notes to track important conversations, observations, and
              details about this deal
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Create your first note
            </Button>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {notes.map((note) => {
              const typeConfig = getTypeConfig(note.type || "general");
              const Icon = typeConfig.icon;

              return (
                <div
                  key={note.id}
                  className="group p-4 bg-background rounded-lg border border-border hover:border-border/80 transition-colors shadow-sm hover:shadow"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div
                        className={`p-2 rounded-lg ${typeConfig.color.split(" ")[0]} ${typeConfig.color.split(" ")[1]}`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant="secondary"
                            className="text-xs font-normal capitalize"
                          >
                            {typeConfig.label}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(note.timestamp || note.createdAt)}
                          </span>
                        </div>

                        {note.author && (
                          <p className="text-xs font-medium text-foreground mb-1">
                            By: {note.author}
                          </p>
                        )}

                        <p className="text-sm text-foreground whitespace-pre-wrap mt-2">
                          {note.content}
                        </p>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => setDeleteId(note.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {notes.length > 0 && !isAdding && (
          <div className="pt-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground hover:text-foreground"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add another note
            </Button>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
